import React, { Component } from 'react';

import PropTypes from 'prop-types';

import { loadRules, reloadRules } from 'common/actions/jiraRules';
import AJAX from 'common/AJAX';
import NoteBox from 'common/common/NoteBox';
import { ActiveIntegrationContext } from 'common/containers/ActiveIntegrationsContainer';
import { CompanyContext } from 'common/containers/CompanyContainer';
import asyncConnect from 'common/core/asyncConnect';
import Data from 'common/Data';
import Dropdown from 'common/Dropdown';
import Helmet from 'common/helmets/Helmet';
import Button from 'common/inputs/Button';
import PostStatus from 'common/post/PostStatus';
import withAccessControl from 'common/routing/withAccessControl';
import Spinner from 'common/Spinner';
import AdminFeatureUpsell from 'common/subdomain/admin/AdminFeatureUpsell';
import AdminIntegrationLimitUpsell from 'common/subdomain/admin/AdminIntegrationLimitUpsell';
import Tappable from 'common/Tappable';
import UppercaseHeader from 'common/UppercaseHeader';
import { RoutePermissions, testEveryPermission } from 'common/util/permissions';
import stringSort from 'common/util/stringSort';
import withContexts from 'common/util/withContexts';

import 'css/components/subdomain/admin/_AdminJiraSettings.scss';

@withContexts(
  {
    activeIntegrations: ActiveIntegrationContext,
    company: CompanyContext,
  },
  {
    forwardRef: true,
  }
)
class AdminJiraSettings extends Component {
  static propTypes = {
    company: PropTypes.shape({
      _id: PropTypes.string,
      subdomain: PropTypes.string,
    }),
  };

  state = {
    any: true,
    error: null,
    saving: false,
    showComposer: false,
    statuses: null,
  };

  constructor(props, context) {
    super(props, context);

    this.anyAllDropdownRef = React.createRef();
    this.cannyStatusDropdownRef = React.createRef();
    this.jiraStatusDropdownRef = React.createRef();
    this.notifyVotersDropdownRef = React.createRef();
  }

  componentDidMount() {
    this.fetchJiraStatuses();
  }

  fetchJiraStatuses = () => {
    Data.fetch(
      {
        statuses: {
          query: Data.queries.jiraStatuses,
        },
      },
      (error, data) => {
        if (error) {
          this.setState({
            error: error,
          });
          return;
        }

        this.setState({
          statuses: data.statuses.sort(stringSort('name')),
        });
      }
    );
  };

  onAnyAllChange = (anyAll) => {
    this.setState({
      any: anyAll === 'any',
    });
  };

  onCloseComposer = () => {
    this.setState({
      showComposer: false,
    });
  };

  onCreateRule = () => {
    this.setState({
      showComposer: true,
    });
  };

  onDeleteRule = (rule) => {
    AJAX.post(
      '/api/jira/deleteRule',
      {
        ruleID: rule._id,
      },
      (response) => {
        if (response !== 'success') {
          this.setState({
            error: 'Something went wrong, please try again later',
          });
        } else {
          const { reloadRules } = this.props;
          reloadRules().then(() => {
            this.setState({
              error: null,
            });
          });
        }
      }
    );
  };

  onSaveRule = () => {
    const allLinkedIssues = this.anyAllDropdownRef.current.getValue() === 'all';
    const cannyStatus = this.cannyStatusDropdownRef.current.getValue();
    const jiraStatus = this.jiraStatusDropdownRef.current.getValue();
    const shouldNotifyVoters = this.notifyVotersDropdownRef.current.getValue() === 'notify';

    this.setState({
      saving: true,
    });

    AJAX.post(
      '/api/jira/createRule',
      {
        allLinkedIssues,
        cannyStatus,
        jiraStatus,
        shouldNotifyVoters,
      },
      (response) => {
        if (response !== 'success') {
          this.setState({
            error: 'Something went wrong, please try again later',
            saving: false,
          });
        } else {
          const { reloadRules } = this.props;
          reloadRules().then(() => {
            this.setState({
              error: null,
              saving: false,
              showComposer: false,
            });
          });
        }
      }
    );
  };

  renderComposer() {
    const { showComposer } = this.state;
    if (!showComposer) {
      return null;
    }

    const { company } = this.props;
    const { any, saving, statuses } = this.state;
    const areStatusesLoaded = statuses && statuses.length;
    const cannyStatusOptions = company.statuses.map((status) => {
      return {
        name: status.name,
        render: <PostStatus showOpen={true} status={status.name} />,
      };
    });

    return (
      <div className="composerContainer">
        <div className="composer">
          When
          <Dropdown
            className="anyAllDropdown"
            defaultSelectedName={'any'}
            dropdownClassName="adminJiraSettingsDropdown"
            onChange={this.onAnyAllChange}
            options={[
              {
                name: 'any',
                render: 'any',
              },
              {
                name: 'all',
                render: 'all',
              },
            ]}
            ref={this.anyAllDropdownRef}
          />
          {any ? 'linked Jira issue is changed to' : 'linked Jira issues are changed to'}
          {areStatusesLoaded ? (
            <Dropdown
              className="jiraStatusDropdown"
              defaultSelectedName={statuses[0].name}
              dropdownClassName="adminJiraSettingsDropdown"
              options={statuses.map((status) => ({
                name: status.name,
                render: status.name,
              }))}
              ref={this.jiraStatusDropdownRef}
            />
          ) : (
            <Spinner />
          )}
          , change all linked Canny posts to
          <Dropdown
            className="cannyStatusDropdown"
            defaultSelectedName="open"
            dropdownClassName="adminJiraSettingsDropdown"
            options={cannyStatusOptions}
            ref={this.cannyStatusDropdownRef}
          />
          , and
          <Dropdown
            className="notifyDropdown"
            defaultSelectedName="notify"
            dropdownClassName="adminJiraSettingsDropdown"
            options={[
              {
                name: 'notify',
                render: 'notify',
              },
              {
                name: 'do not notify',
                render: 'do not notify',
              },
            ]}
            ref={this.notifyVotersDropdownRef}
          />
          voters.
        </div>
        <div className="buttons">
          <Button buttonType="ghostButton" onTap={this.onCloseComposer} value="cancel" />
          <Button loading={saving} onTap={this.onSaveRule} value="Create" />
        </div>
      </div>
    );
  }

  renderCreateRuleButton() {
    const { showComposer } = this.state;
    if (showComposer) {
      return null;
    }

    return (
      <Button className="createRuleButton" onTap={this.onCreateRule} value="Create New Rule" />
    );
  }

  renderRules() {
    const { rules } = this.props;
    const items = rules.map((rule, i) => (
      <div className="rule" key={rule._id}>
        <div className="description">
          {'When '}
          {rule.allLinkedIssues ? 'all ' : 'any '}
          {rule.allLinkedIssues
            ? 'linked Jira issues are changed to '
            : 'linked Jira issue is changed to '}
          {'"' + rule.jiraStatus + '"'}
          {', change all linked Canny posts to '}
          <PostStatus showOpen={true} status={rule.cannyStatus} />
          {', and '}
          {rule.shouldNotifyVoters ? 'notify\u00a0voters' : 'do not notify\u00a0voters'}
          {'.'}
        </div>
        <Tappable onTap={this.onDeleteRule.bind(this, rule)}>
          <div className="icon icon-x" />
        </Tappable>
      </div>
    ));
    return (
      <div className="rules">
        {rules.length === 0 ? null : <UppercaseHeader>Rules</UppercaseHeader>}
        {items}
      </div>
    );
  }

  renderError() {
    const { error } = this.state;
    if (!error) {
      return null;
    }

    return <div className="error">{error}</div>;
  }

  renderConnected() {
    return (
      <div className="connected">
        <div className="text">
          This page lets you configure status syncing with Jira. Set up rules so that when an
          issue's status changes, linked Canny posts are updated as well.
        </div>
        {this.renderRules()}
        {this.renderComposer()}
        {this.renderError()}
        {this.renderCreateRuleButton()}
        <NoteBox title="Note" colorScheme="note" className="text withMarginTop">
          Disconnecting Jira from Canny must be completed from your Jira workspace settings.
          <br />
          <a href="https://help.canny.io/en/articles/1283233-jira-integration">
            View our help article
          </a>{' '}
          for more&nbsp;details.
        </NoteBox>
      </div>
    );
  }

  renderDisconnected() {
    const {
      company,
      activeIntegrations: { integrationCount, integrationLimit },
    } = this.props;
    let upsell;

    if (integrationLimit && integrationCount >= integrationLimit) {
      upsell = <AdminIntegrationLimitUpsell />;
    }

    if (!company?.integrations?.jira) {
      upsell = <AdminFeatureUpsell feature="jira" />;
    }

    return (
      <div className="disconnected">
        <div className="text">
          The Jira integration lets you configure status syncing with Jira once you've set up
          the&nbsp;integration.
        </div>
        {upsell ? (
          upsell
        ) : (
          <div className="text">
            Visit&nbsp;
            <a href="https://help.canny.io/en/articles/1283233-jira-integration">this page</a>
            &nbsp;for instructions on how to set up Canny's Jira&nbsp;integration.
          </div>
        )}
      </div>
    );
  }

  renderContents() {
    const { company } = this.props;

    const disconnected = !company.jira || !company.jira.connected;
    if (disconnected) {
      return this.renderDisconnected();
    }

    return this.renderConnected();
  }

  render() {
    return (
      <div className="adminJiraSettings">
        <Helmet title="Jira Integration | Canny" />
        <div className="settingsHeading">Jira Integration</div>
        <div className="content">{this.renderContents()}</div>
      </div>
    );
  }
}

export default asyncConnect(
  [
    {
      promise: ({ store: { dispatch, getState } }) => {
        return dispatch(loadRules());
      },
    },
  ],
  (state) => ({
    rules: state.jiraRules && state.jiraRules.data,
  }),
  (dispatch) => ({
    reloadRules: (post) => {
      return Promise.all([dispatch(reloadRules())]);
    },
  })
)(
  withAccessControl(
    AdminJiraSettings,
    testEveryPermission(RoutePermissions.integrations.jira),
    '/admin/settings'
  )
);
